import { type Message } from '../context/chatOverlayContext';
import {
  type AskApiErrorResponse,
  type AskApiResponse,
  type SampleDataAPIResponse,
  type SampleDataErrorResponse,
} from '../data-layer/types';

import config from './ispmConfig';

// chatHistory.ts
const CHAT_HISTORY_KEY = 'chatHistory';

// Function to get chat history from session storage
export const getChatHistory = (): Message[] => {
  const history = sessionStorage.getItem(CHAT_HISTORY_KEY);

  return history ? (JSON.parse(history) as Message[]) : [];
};

export const clearChatHistory = (): void => {
  sessionStorage.removeItem(CHAT_HISTORY_KEY);
};

// Function to save chat history to session storage
export const saveChatHistory = (chatHistory: Message[]): void => {
  sessionStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(chatHistory));
};

// Function to add a new message to the chat history
export const addMessageToHistory = (message: Message): Message[] =>
  storeChatHistoryEfficient(message);

function hasContentType(
  data: AskApiResponse | SampleDataAPIResponse | undefined
): data is
  | Exclude<AskApiResponse, AskApiErrorResponse>
  | Exclude<SampleDataAPIResponse, SampleDataErrorResponse> {
  return !!data && 'contentType' in data;
}

export const storeChatHistoryEfficient = (message: Message): Message[] => {
  // Parse existing chat history from session storage
  const chatHistory: Message[] = getChatHistory();

  chatHistory.push(message);

  // Track the latest indices
  const recentSampleDataIndices: number[] = [];
  const recentChartIndices: number[] = [];

  // Traverse the chat history in reverse order
  for (
    let currentIndex = chatHistory.length - 1;
    currentIndex >= 0;
    currentIndex -= 1
  ) {
    const currentEntry = chatHistory[currentIndex];

    if (!currentEntry.isUser && hasContentType(currentEntry.data)) {
      // Handle "sampleData" entries
      if (currentEntry.data.contentType === 'sampleData') {
        if (recentSampleDataIndices.length < config.RECENT_TABLE_STORAGE) {
          // Track the last 2 "sampleData" entries
          recentSampleDataIndices.push(currentIndex);
        } else {
          // Set content to an empty array for older "sampleData" entries (keeping only 2)
          currentEntry.data.content = []; // Empty content of the older "sampleData" entry
        }
      }

      // Handle "chart" entries
      if (currentEntry.data.contentType === 'chart') {
        if (recentChartIndices.length < config.RECENT_CHART_STORAGE) {
          // Keep track of the two most recent "chart" entries
          recentChartIndices.push(currentIndex);
        } else {
          // Remove content for older "chart" entries
          currentEntry.data.content = '';
        }
      }
    }
  }

  // Save the updated chat history back to session storage
  saveChatHistory(chatHistory);

  return chatHistory;
};
